import React from "react";
import { Link } from "react-router-dom";
import { aboutPreview, siteConfig, stats, testimonials } from "../data/mockData";
import { CheckCircle, Star, Phone, Award, Shield, Users, Calendar } from "lucide-react";

const AboutStats = () => {
  const statItems = [
    { id: "rating", icon: Star, label: "Google Rating", value: stats.reviewScore },
    { id: "clients", icon: Users, label: "Happy Clients", value: `${stats.totalReviews}+` },
    { id: "states", icon: Shield, label: "States Licensed", value: stats.statesLicensed },
    { id: "experience", icon: Award, label: "Years Experience", value: stats.yearsExperience },
  ];

  return (
    <section className="py-16 bg-white">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
          {statItems.map((stat) => (
            <div key={stat.id} className="text-center">
              <div className="w-12 h-12 bg-[#E8F5E9] rounded-xl flex items-center justify-center mx-auto mb-3">
                <stat.icon size={24} className="text-[#3A7D44]" />
              </div>
              <p className="text-3xl font-bold text-[#1B2A1B]">{stat.value}</p>
              <p className="text-sm text-[#5A6B5A] mt-1">{stat.label}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const AboutApproach = () => (
  <section className="py-20 bg-[#F9F7F4]">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl mx-auto">
        <h2
          className="text-3xl font-bold text-[#1B2A1B] mb-8"
          style={{ fontFamily: "'DM Sans', sans-serif" }}
        >
          My Approach
        </h2>
        <div className="space-y-6 text-[#5A6B5A] leading-relaxed">
          <p>
            I believe insurance should be simple and straightforward. When we work
            together, I start by listening — really listening — to understand your
            specific situation, your health needs, your budget, and your goals.
          </p>
          <p>
            Then I go to work. As an independent broker with access to multiple
            carriers, I compare plans side by side to find the best combination of
            coverage and value for your unique situation. I'll explain each option
            in clear, straightforward terms, answer your questions honestly, and never push you
            toward something that isn't right for you.
          </p>
          <p>
            My job doesn't end when you enroll. I'm here year-round to help with
            questions, assist with claims, and review your coverage as your needs
            change. Think of me as your go-to person for anything health
            insurance.
          </p>
        </div>
        <ul className="mt-8 space-y-3">
          {aboutPreview.highlights.map((item) => (
            <li key={item} className="flex items-center gap-3">
              <CheckCircle size={18} className="text-[#3A7D44] flex-shrink-0" />
              <span className="text-[#5A6B5A]">{item}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  </section>
);

const AboutSEOContent = () => (
  <section className="py-20 bg-white">
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
      <h2
        className="text-3xl sm:text-4xl font-bold text-[#1B2A1B] mb-6 leading-tight"
        style={{ fontFamily: "'DM Sans', sans-serif" }}
      >
        Independent Health Insurance Broker in Raleigh, NC
      </h2>
      <p className="text-[#5A6B5A] leading-relaxed text-lg mb-10">
        Hess Insurance Solutions is a trusted, independent health insurance broker
        in Raleigh, NC, with over 20 years of combined industry experience helping
        individuals, families, and small business owners navigate an insurance
        market that keeps changing — and keeps getting more expensive.
      </p>

      <h3 className="text-2xl font-bold text-[#1B2A1B] mb-4" style={{ fontFamily: "'DM Sans', sans-serif" }}>
        What We Specialize In
      </h3>
      <div className="space-y-5 text-[#5A6B5A] leading-relaxed mb-10">
        <p>
          Are you retired but not yet 65? Self-employed? Paying out of pocket for
          health insurance, or leaving the Marketplace? Need employee benefits for
          your team? We specialize in private health insurance for individuals,
          families, and small businesses — plans based on your health, not your
          income.
        </p>
        <p>
          As health insurance options evolve and prices rise, we focus on finding
          the most innovative, practical approaches to healthcare coverage — not
          just the first plan that fits.
        </p>
      </div>

      <h3 className="text-2xl font-bold text-[#1B2A1B] mb-4" style={{ fontFamily: "'DM Sans', sans-serif" }}>
        2026 Small Business Benefits
      </h3>
      <p className="text-[#5A6B5A] leading-relaxed mb-10">
        Small business owners in the Raleigh area should ask about the newest 2026
        employee benefits programs. Options and pricing change year to year, and
        many owners are paying for outdated plan structures without realizing
        better ones exist.
      </p>

      <h3 className="text-2xl font-bold text-[#1B2A1B] mb-4" style={{ fontFamily: "'DM Sans', sans-serif" }}>
        Medicare Guidance
      </h3>
      <p className="text-[#5A6B5A] leading-relaxed">
        For clients approaching Medicare age, we provide education and referrals
        to trusted, licensed Medicare specialists. Hess Insurance Solutions does
        not sell or enroll Medicare plans — we make sure you're connected with
        someone who specializes specifically in that market.
      </p>
    </div>
  </section>
);

const AboutFeaturedReview = () => (
  <section className="py-20 bg-white">
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
      <div className="flex justify-center mb-4">
        {[...Array(5)].map((_, i) => (
          <Star key={`about-star-${i}`} size={24} className="text-yellow-500 fill-yellow-500" />
        ))}
      </div>
      <blockquote className="text-xl text-[#1B2A1B] italic leading-relaxed mb-6">
        "{testimonials[0].text}"
      </blockquote>
      <p className="text-[#3A7D44] font-semibold">{testimonials[0].name}</p>
    </div>
  </section>
);

const AboutPage = () => {
  return (
    <>
      {/* Hero */}
      <section className="pt-28 pb-16 bg-gradient-to-b from-[#F1F8F2] to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-sm font-semibold text-[#3A7D44] uppercase tracking-wider">
                Meet Your Broker
              </span>
              <h1
                className="text-4xl sm:text-5xl font-bold text-[#1B2A1B] mt-3 mb-6"
                style={{ fontFamily: "'DM Sans', sans-serif" }}
              >
                Kimberly Hess
              </h1>
              <p className="text-base font-semibold text-[#3A7D44] mb-4">
                Licensed Health & Life Insurance Broker
              </p>
              <p className="text-lg text-[#5A6B5A] leading-relaxed mb-4">
                {aboutPreview.bio}
              </p>
              <p className="text-[#5A6B5A] leading-relaxed mb-4">
                {aboutPreview.bioExtended}
              </p>
              <div className="bg-[#F1F8F2] rounded-xl p-6 mb-6">
                <p className="text-sm font-semibold text-[#3A7D44] mb-3">What I bring to the table:</p>
                <ul className="space-y-2">
                  {aboutPreview.highlights.map((highlight) => (
                    <li key={highlight} className="flex items-start gap-2 text-[#5A6B5A]">
                      <CheckCircle size={18} className="text-[#3A7D44] mt-0.5 flex-shrink-0" />
                      <span className="text-sm">{highlight}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <p className="text-[#5A6B5A] leading-relaxed mb-8 italic">
                Because at the end of the day, protecting your health isn't just about coverage — it's about protecting your life, your business, and your peace of mind.
              </p>
              <div className="flex flex-wrap gap-4">
                <a
                  href={siteConfig.calendlyUrl}
                  
                  
                  className="inline-flex items-center gap-2 px-6 py-3 text-sm font-semibold text-white bg-[#3A7D44] rounded-lg hover:bg-[#2D5A35] transition-colors"
                >
                  <Calendar size={16} />
                  Book a Call
                </a>
                <a
                  href={`tel:${siteConfig.phone}`}
                  className="inline-flex items-center gap-2 px-6 py-3 text-sm font-semibold text-[#3A7D44] border border-[#3A7D44] rounded-lg hover:bg-[#F1F8F2] transition-colors"
                >
                  <Phone size={16} />
                  {siteConfig.phoneFormatted}
                </a>
              </div>
            </div>
            <div className="relative">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src={siteConfig.kimAboutPhoto}
                  alt="Kimberly Hess"
                  className="w-full max-w-md mx-auto object-cover"
                />
              </div>
              <div className="absolute -z-10 -bottom-6 -right-6 w-full h-full bg-[#E8F5E9] rounded-2xl" />
            </div>
          </div>
        </div>
      </section>

      <AboutStats />
      <AboutSEOContent />
      <AboutApproach />
      <AboutFeaturedReview />

      {/* CTA */}
      <section className="py-20 bg-[#3A7D44]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-4" style={{ fontFamily: "'DM Sans', sans-serif" }}>
            Let's Find Your Perfect Plan
          </h2>
          <p className="text-white/80 mb-8">
            Ready to explore your options? I'd love to help.
          </p>
          <a
            href={siteConfig.calendlyUrl}
            
            
            className="inline-flex items-center gap-2 px-8 py-4 text-base font-semibold text-[#3A7D44] bg-white rounded-lg hover:bg-gray-50 transition-colors"
          >
            Get Started Today
          </a>
        </div>
      </section>
    </>
  );
};

export default AboutPage;
